import { useEffect, useState } from 'react';
import { useUserStore } from '../../stores/user-store';
import { computePreloadWindowIds } from './mp3-preload-window';

// Continuous IntersectionObserver over the result cards (#scrollTarget >
// [data-preload-index]). Returns the set of song ids in the rolling preload
// window (see mp3-preload-window.ts). White-label only; for non-WL users it
// returns an empty set and attaches no observer (zero overhead).
//
// Distinct from useInView, which fires once then unobserves — that cannot track
// a back-and-forth scroll window.
export function useMp3PreloadWindow(
  songs: ReadonlyArray<{ id: string }>,
): Set<string> {
  const isWhiteLabel = useUserStore(s => s.isUserWhiteLabel);
  const [windowIds, setWindowIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!isWhiteLabel || songs.length === 0) {
      setWindowIds(new Set());
      return;
    }

    // NOTE(port): two nested elements carry id="scrollTarget" (the outer wrapper
    // in MainContent and the inner grid in ResultsGrid). getElementById returns
    // the outer one; this hook works regardless because querySelectorAll below
    // recurses through descendants to find the cards — the matched #scrollTarget
    // is not necessarily the cards' direct parent.
    const container = document.getElementById('scrollTarget');
    if (!container) {
      return;
    }

    const visible = new Set<number>();
    let throttled = false;
    let throttleTimer: ReturnType<typeof setTimeout> | undefined;

    const recompute = () => {
      const anchor = visible.size ? Math.min(...visible) : null;
      setWindowIds(computePreloadWindowIds(songs, anchor));
    };

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const idx = Number((entry.target as HTMLElement).dataset.preloadIndex);
          if (Number.isNaN(idx)) {
            continue;
          }
          if (entry.isIntersecting) {
            visible.add(idx);
          } else {
            visible.delete(idx);
          }
        }
        // Throttle recompute (100ms leading edge) — same pattern as the
        // infinite-scroll handler in ResultsGrid.
        if (throttled) {
          return;
        }
        throttled = true;
        recompute();
        throttleTimer = setTimeout(() => {
          throttled = false;
        }, 100);
      },
      // Downward bias: cards about to enter from below count as visible, so the
      // next songs warm up before they are on screen.
      { rootMargin: '0px 0px 50% 0px' },
    );

    container
      .querySelectorAll<HTMLElement>('[data-preload-index]')
      .forEach(card => observer.observe(card));

    return () => {
      clearTimeout(throttleTimer);
      observer.disconnect();
    };
  }, [isWhiteLabel, songs]);

  return windowIds;
}
